import { useEffect, useRef, useState } from "react";

import styles from "../styles/delivery-regions.module.scss";

import DeliveryRegion from "../shared/plugins/core/schema/configurations/DeliveryRegion";

import { Button } from "../shared/components/Button";
import { Title } from "../shared/components/Title";

import ROLES from "data/Roles";
import { EnvironmentsAPI } from "../providers/EnvironmentsAPI";
import { CurrencyInput } from "../shared/components/CurrencyInput";
import { Upload } from "../shared/components/Icons";
import { NumberInput } from "../shared/components/NumberInput";
import { Icon } from "../shared/components/core/Icon";
import FreeDeliveryDrawer from "../shared/containers/Settings/Drawers/FreeDeliveryDrawer";
import KMLDeliveryDrawer from "../shared/containers/Settings/Drawers/KMLDeliveryDrawer";
import { EnvironmentStorage } from "../shared/tools/EnvironmentStorage";
import { UserStorage } from "../shared/tools/UserStorage";

export default function DeliveryRegions() {
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState(
    new DeliveryRegion([DeliveryRegion.getTemplateRange(0.1)]).ranges
  );
  const [counter, setCounter] = useState(100);
  const [environment, setEnvironment] = useState({});
  const [openUpload, setOpenUpload] = useState(false);
  const [openFreeDelivery, setOpenFreeDelivery] = useState(false);
  const [fileInfos, setFileInfos] = useState({});
  const [fileUploaded, setFileUploaded] = useState(false);
  const [hasFileUploaded, setHasFileUploaded] = useState(false);
  const [deliveryPolygons, setDeliveryPolygons] = useState([]);
  const intervalRef = useRef(null);

  let userRole = UserStorage.getLoggedUser()?.role;
  if (!userRole) userRole = ROLES.SHOPKEEPER;

  const canUpdate = userRole !== ROLES.SHOPKEEPER;
  const canUpdateTime = true;

  useEffect(() => {
    setEnvironment(EnvironmentStorage.getEnvironment());
    const loadData = async () => {
      const environment_id = EnvironmentStorage.getSelectedEnvironment();
      const response = await EnvironmentsAPI.getConfigurationsDeliveryRegions(
        String(environment_id)
      );
      let data = response.getData({});
      data = !data.length
        ? new DeliveryRegion([DeliveryRegion.getTemplateRange()]).ranges
        : new DeliveryRegion(data).ranges;
      setRegions(data);
    };
    loadData();
  }, []);

  const addRegion = () => {
    const newRegion = parseFloat(
      (+regions[regions.length - 1].upTo + 0.1).toFixed(2)
    );

    setRegions([...regions, DeliveryRegion.getTemplateRange(newRegion)]);
  };

  const save = async () => {
    await handleSubmit();
  };

  const changeFreeFee = async (value, index) => {
    const copyRegions = [...regions];

    copyRegions[index].freeFee = value;

    setRegions(copyRegions);
  };

  const changePrice = async (value, index) => {
    regions[index].price = parseFloat(value);
    setRegions(regions);
  };

  const changeMinOrder = async (value, index) => {
    regions[index].minOrder = parseFloat(value);
    setRegions(regions);
  };

  const changeMinDelivery = async (value, index) => {
    regions[index].minDelivery = parseFloat(value);
    setRegions(regions);
  };

  const changeMaxDelivery = async (value, index) => {
    regions[index].maxDelivery = parseFloat(value);
    setRegions(regions);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const environment_id = EnvironmentStorage.getSelectedEnvironment();
    await EnvironmentsAPI.putConfigurationsDeliveryRegionsByEnvironmentId(
      environment_id,
      regions
    );
    setLoading(false);
  };

  const getDistance = (value) => {
    let numberFormater = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
    });

    return `${numberFormater.format((+value).toFixed(2))} km`;
  };

  useEffect(() => {
    const fetchDeliveryPolygons = async () => {
      try {
        const selectedEnvironment =
          await EnvironmentStorage.getSelectedEnvironment();
        const deliveryPolygons = await EnvironmentsAPI.getConfigurationsByAlias(
          selectedEnvironment,
          "delivery_polygons"
        );

        setDeliveryPolygons(
          Array.isArray(deliveryPolygons) ? deliveryPolygons : []
        );
      } catch (error) {
        console.log(error);
      }
    };

    fetchDeliveryPolygons();
  }, []);

  const startPressedCounter = (index, option = "increase") => {
    if (intervalRef.current) return;

    const value = option === "increase" ? 0.1 : -0.1;

    intervalRef.current = setInterval(() => {
      setCounter((prevCounter) => prevCounter + 1);
      const copyRegions = [...regions];

      if (index === 0 && +copyRegions[index].upTo + value < 0.1) return false;

      if (
        index >= 1 &&
        +regions[index].upTo + value <= +copyRegions[index - 1].upTo
      ) {
        return false;
      }

      copyRegions[index].upTo = parseFloat(
        (+copyRegions[index].upTo + value).toFixed(2)
      );

      setRegions(copyRegions);
    }, 250);
  };

  const stopPressedCounter = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const changeRadius = (index, option = "increase") => {
    const value = option === "increase" ? 0.1 : -0.1;

    const copyRegions = [...regions];

    if (index === 0 && +copyRegions[index].upTo + value < 0.1) return false;

    if (
      index >= 1 &&
      +regions[index].upTo + value <= +copyRegions[index - 1].upTo
    ) {
      return false;
    }

    copyRegions[index].upTo = parseFloat(
      (+copyRegions[index].upTo + value).toFixed(2)
    );

    setRegions(copyRegions);
  };

  const removeLastRegion = () => {
    setRegions(regions.slice(0, -1));
  };

  const getSubtitle = () => {
    if (environment?.brand)
      return (
        `${environment.brand.name} - ${environment.name}` +
        (environment.active === false ? " (Inativa)" : "")
      );
    return environment?.name;
  };

  return (
    <div className={styles.deliveryRegionsContainer}>
      <Title
        title="Regiões de entrega"
        subtitle={getSubtitle()}
        backTo={{ url: "/configuracoes", name: "Configurações" }}
      >
        {canUpdate && (
          <>
            {(deliveryPolygons.length > 0 || UserStorage.isGodMode) && (
              <Button
                design="default"
                onClick={() => setOpenUpload(true)}
                className="mr-15"
              >
                <i className="far fa-map-marked-alt" />
              </Button>
            )}

            {UserStorage.isGodMode && (
              <Button
                design="default"
                onClick={() => setOpenFreeDelivery(true)}
                className="mr-15"
              >
                Frete grátis
              </Button>
            )}
            <Button design="primary" onClick={() => addRegion()} add>
              Adicionar região
            </Button>
          </>
        )}
      </Title>
      <div className={styles.regions}>
        {regions.map((region, index) => {
          return (
            <div className={styles.region} key={index}>
              <div className={styles.distances}>
                <div className="d-flex align-center justify-between mb-20">
                  <p className={styles.regionName}>Região {index + 1}</p>
                  {canUpdate &&
                    index === regions.length - 1 &&
                    regions.length > 1 && (
                      <Button
                        design="default-icon-mini-2"
                        title="Remover região"
                        onClick={() => removeLastRegion()}
                      >
                        <Icon name="trash-alt" size="1x" />
                      </Button>
                    )}
                </div>

                <div className={styles.distance}>
                  <span>De</span>
                  <p className={styles.km}>
                    {getDistance(
                      regions[index - 1] ? regions[index - 1].upTo || 0 : 0
                    )}
                  </p>
                </div>
                <div className={`${styles.distance} justify-between`}>
                  <div className="d-flex align-center">
                    <span>Até</span>
                    <p className={styles.km}>{getDistance(region.upTo)}</p>
                  </div>

                  {canUpdate && index === regions.length - 1 && (
                    <div className={styles.increaseDecrease}>
                      <div
                        className={`${styles.button} ${
                          region.upTo >=
                            (
                              (regions[index - 1]
                                ? +regions[index - 1].upTo || 0
                                : 0) + 0.2
                            ).toFixed(2) && index === regions.length - 1
                            ? styles.enabled
                            : styles.disabled
                        }`}
                        onMouseDown={() =>
                          startPressedCounter(index, "decrease")
                        }
                        onTouchStart={() =>
                          startPressedCounter(index, "decrease")
                        }
                        onTouchEnd={stopPressedCounter}
                        onMouseUp={stopPressedCounter}
                        onMouseLeave={stopPressedCounter}
                        onClick={() => changeRadius(index, "decrease")}
                      >
                        <div className={`${styles.decrease}`}>
                          <Icon name="minus" color="#999" />
                        </div>
                      </div>

                      <div
                        className={styles.button}
                        onMouseDown={() =>
                          startPressedCounter(index, "increase")
                        }
                        onTouchStart={() =>
                          startPressedCounter(index, "increase")
                        }
                        onTouchEnd={stopPressedCounter}
                        onMouseUp={stopPressedCounter}
                        onMouseLeave={stopPressedCounter}
                        onClick={() => changeRadius(index, "increase")}
                      >
                        <div className={styles.increase}>
                          <Icon name="plus" color="#999" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.column}>
                <CurrencyInput
                  name={`price${index}`}
                  design="three"
                  className="mb-20"
                  floatLabel="Valor"
                  onChange={(e, m, value) => changePrice(value, index)}
                  value={region.price}
                  disabled={canUpdate === false}
                  // suffix={
                  //   <Checkbox
                  //     name={`freeFee${index}`}
                  //     design="two"
                  //     checked={region.freeFee}
                  //     onChange={(value) => changeFreeFee(value, index)}
                  //     disabled={canUpdate === false}
                  //   >
                  //     Taxa grátis
                  //   </Checkbox>
                  // }
                />
                <CurrencyInput
                  name={`minOrder${index}`}
                  design="three"
                  floatLabel="Pedido mínimo"
                  onChange={(e, m, value) => changeMinOrder(value, index)}
                  value={region.minOrder}
                />
              </div>

              <div className={styles.column}>
                <NumberInput
                  name={`minDelivery${index}`}
                  design="three"
                  className="mb-20"
                  floatLabel="Tempo mínimo para entrega"
                  suffix={" min"}
                  disabled={canUpdateTime === false}
                  onChange={(e, m, value) => changeMinDelivery(value, index)}
                  value={region.minDelivery}
                />
                <NumberInput
                  name={`maxDelivery${index}`}
                  design="three"
                  floatLabel="Tempo máximo para entrega"
                  suffix={" min"}
                  disabled={canUpdateTime === false}
                  onChange={(e, m, value) => changeMaxDelivery(value, index)}
                  value={region.maxDelivery}
                />
              </div>
            </div>
          );
        })}
      </div>

      {(canUpdate || canUpdateTime) && (
        <div className={styles.save}>
          <Button
            design="primary"
            onClick={save}
            loading={loading}
            disabled={loading}
            className="w-100"
          >
            Salvar
          </Button>
        </div>
      )}

      <KMLDeliveryDrawer
        visible={openUpload}
        onSave={() => setOpenUpload(false)}
        onClose={() => setOpenUpload(false)}
      />

      <FreeDeliveryDrawer
        visible={openFreeDelivery}
        onSave={() => setOpenFreeDelivery(false)}
        onClose={() => setOpenFreeDelivery(false)}
      />
    </div>
  );
}
